// src/components/order/ProductSelectionModal.jsx

import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';

Modal.setAppElement('#root');

const ProductSelectionModal = ({ isOpen, onClose, products, onSelectProduct, title = "Select Product", buttonText = "Add to List" }) => {
    // --- STATE MANAGEMENT (Enhanced for multi-color selection) ---
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedProducts, setSelectedProducts] = useState({});
    const [viewImageUrl, setViewImageUrl] = useState(null);
    const [selectedColorVariants, setSelectedColorVariants] = useState({});
    // Enhanced state for multi-color selection: { productId: { colorVariantIndex: { sizes: [sizes], colorVariant: variantData } } }
    const [selectedColorsAndSizes, setSelectedColorsAndSizes] = useState({});
    // Track which products are expanded for better UX
    const [expandedProducts, setExpandedProducts] = useState({});



    // Reset state when the modal closes
    useEffect(() => {
    if (!isOpen) {
        setSearchQuery("");
        setSelectedProducts({});
        setSelectedColorVariants({});
            setSelectedColorsAndSizes({});
            setExpandedProducts({});
    }
}, [isOpen]);


    // --- HELPERS ---
    const filteredProducts = products.filter(product =>
        product.designName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Helper function to get product image (handles both data structures)
    const getProductImage = (product) => {
        // Check if product has colorVariants (new structure)
        if (product.colorVariants && Array.isArray(product.colorVariants) && product.colorVariants.length > 0) {
            const selectedColorVariant = selectedColorVariants[product.id];
            if (selectedColorVariant && product.colorVariants[selectedColorVariant]) {
                return product.colorVariants[selectedColorVariant].images?.front || product.colorVariants[selectedColorVariant].images?.front;
            }
            // Default to first color variant if none selected
            return product.colorVariants[0].images?.front || product.colorVariants[0].images?.front;
        }
        // Fallback to old structure
        return product.images?.front || product.frontImage || product.image;
    };

    // Helper function to get available sizes (handles both data structures)
    const getAvailableSizes = (product) => {
        // Check if product has colorVariants (new structure)
        if (product.colorVariants && Array.isArray(product.colorVariants) && product.colorVariants.length > 0) {
            const selectedColorVariant = selectedColorVariants[product.id];
            if (selectedColorVariant !== undefined && product.colorVariants[selectedColorVariant]) {
                const skus = product.colorVariants[selectedColorVariant].SKUs || [];
                return extractSizesFromSKUs(skus);
            }
            // Default to first color variant if none selected
            const skus = product.colorVariants[0].SKUs || [];
            return extractSizesFromSKUs(skus);
        }
        // Fallback to old structure
        const skus = product.SKUs || [];
        return extractSizesFromSKUs(skus);
    };

    // Helper function to extract sizes from SKUs
    const extractSizesFromSKUs = (skus) => {
        if (!Array.isArray(skus)) return ['S', 'M', 'L', 'XL', 'XXL'];
        
        const sizes = new Set();
        skus.forEach(sku => {
            const parts = sku.split('-');
            if (parts.length >= 3) {
                // For format like "u-677743-S-white", size is in second-to-last position
                // For format like "u-677743-S", size is in last position
                const possibleSize = parts[parts.length - 2]; // Second-to-last position
                if (['XS', 'S', 'M', 'L', 'XL', 'XXL'].includes(possibleSize)) {
                    sizes.add(possibleSize);
                } else {
                    // Fallback to last position for different formats
                    const lastPositionSize = parts[parts.length - 1];
                    if (['XS', 'S', 'M', 'L', 'XL', 'XXL'].includes(lastPositionSize)) {
                        sizes.add(lastPositionSize);
                    }
                }
            }
        });
        
        const sizeArray = Array.from(sizes);
        return sizeArray.length > 0 ? sizeArray : ['S', 'M', 'L', 'XL', 'XXL'];
    };

    // Helper function to get SKUs for selected color variant
    const getSKUsForColorVariant = (product, colorVariantIndex) => {
        if (product.colorVariants && Array.isArray(product.colorVariants) && product.colorVariants[colorVariantIndex]) {
            return product.colorVariants[colorVariantIndex].SKUs || [];
        }
        return product.SKUs || [];
    };

    // Helper function to get available sizes for a specific color variant
    const getAvailableSizesForColorVariant = (product, colorVariantIndex) => {
        const skus = getSKUsForColorVariant(product, colorVariantIndex);
        return extractSizesFromSKUs(skus);
    };

    // Helper function to check if a color variant is selected for a product
    const isColorVariantSelected = (productId, colorVariantIndex) => {
        return selectedColorsAndSizes[productId] && 
               selectedColorsAndSizes[productId][colorVariantIndex] && 
               selectedColorsAndSizes[productId][colorVariantIndex].sizes &&
               selectedColorsAndSizes[productId][colorVariantIndex].sizes.length > 0;
    };

    // Helper function to get selected sizes for a specific color variant
    const getSelectedSizesForColorVariant = (productId, colorVariantIndex) => {
        return selectedColorsAndSizes[productId]?.[colorVariantIndex]?.sizes || [];
    };

    // Helper function to get total selected items for a product
    const getTotalSelectedItemsForProduct = (productId) => {
        if (!selectedColorsAndSizes[productId]) return 0;
        return Object.values(selectedColorsAndSizes[productId]).reduce((total, colorData) => {
            return total + (colorData.sizes ? colorData.sizes.length : 0);
        }, 0);
    };

    // Helper function to check if a product has any selections (for legacy compatibility)
    const hasLegacySelection = (productId) => {
        return selectedProducts[productId] && selectedProducts[productId].sizes && selectedProducts[productId].sizes.length > 0;
    };

    // Helper function to get total selections including legacy
    const getTotalSelectionsForProduct = (productId) => {
        const multiColorCount = getTotalSelectedItemsForProduct(productId);
        const legacyCount = hasLegacySelection(productId) ? selectedProducts[productId].sizes.length : 0;
        return multiColorCount + legacyCount;
    };

    // --- HANDLERS ---
    const toggleProduct = (product) => {
        const hasColorVariants = product.colorVariants && Array.isArray(product.colorVariants) && product.colorVariants.length > 0;
        
        if (hasColorVariants) {
            // For multi-color products, toggle expansion instead of selection
            setExpandedProducts(prev => ({
                ...prev,
                [product.id]: !prev[product.id]
            }));
        } else {
            // For single-color products, use legacy behavior
    setSelectedProducts(prev => {
        const exists = !!prev[product.id];
        if (exists) {
            const newState = { ...prev };
            delete newState[product.id];
            return newState;
        } else {
            return {
                ...prev,
                [product.id]: { product, sizes: [] }
            };
        }
    });
        }
    };

    // Toggle size selection for a specific color variant (enhanced)
    const toggleSizeForColorVariant = (productId, colorVariantIndex, size, colorVariant) => {
        setSelectedColorsAndSizes(prev => {
            const productColors = prev[productId] || {};
            const colorData = productColors[colorVariantIndex] || { sizes: [], colorVariant };
            const currentSizes = colorData.sizes || [];
            
            const updatedSizes = currentSizes.includes(size)
                ? currentSizes.filter(s => s !== size)
                : [...currentSizes, size];
            
            // If no sizes left for this color, remove the color entry
            if (updatedSizes.length === 0) {
                const newProductColors = { ...productColors };
                delete newProductColors[colorVariantIndex];
                return {
                    ...prev,
                    [productId]: Object.keys(newProductColors).length > 0 ? newProductColors : undefined
                };
            }

        return {
            ...prev,
                [productId]: {
                    ...productColors,
                    [colorVariantIndex]: {
                        sizes: updatedSizes,
                        colorVariant: colorVariant
                    }
                }
        };
    });
};

    // Legacy function for backward compatibility (single color selection)
const selectColorVariant = (productId, colorVariantIndex) => {
    setSelectedColorVariants(prev => ({
        ...prev,
        [productId]: colorVariantIndex
    }));
    
    // Reset sizes when color variant changes
    setSelectedProducts(prev => {
        const productEntry = prev[productId];
        if (!productEntry) return prev;
        
        return {
            ...prev,
            [productId]: { ...productEntry, sizes: [] }
        };
    });
};

    // Legacy toggleSize function for single-color products
    const toggleSize = (productId, size) => {
        setSelectedProducts(prev => {
            const productEntry = prev[productId];
            if (!productEntry) return prev;

            const sizes = productEntry.sizes.includes(size)
                ? productEntry.sizes.filter(s => s !== size)
                : [...productEntry.sizes, size];

            return {
                ...prev,
                [productId]: { ...productEntry, sizes }
            };
        });
    };

// Add getSKUForSize helper
const getSKUForSize = (product, size, colorVariantIndex = null) => {
    let skus = [];
    
    // Get SKUs based on data structure
    if (product.colorVariants && Array.isArray(product.colorVariants) && product.colorVariants.length > 0) {
        const variantIndex = colorVariantIndex !== null ? colorVariantIndex : selectedColorVariants[product.id];
        
        if (variantIndex !== undefined && product.colorVariants[variantIndex]) {
            skus = product.colorVariants[variantIndex].SKUs || [];
        } else {
            skus = product.colorVariants[0].SKUs || [];
        }
    } else {
        skus = product.SKUs || [];
    }
    
    if (!Array.isArray(skus)) return null;
    
    const foundSKU = skus.find(sku => {
        const parts = sku.split('-');
        if (parts.length >= 3) {
            // For format like "u-677743-S-white", size is in second-to-last position
            const sizeInSecondToLast = parts[parts.length - 2];
            if (sizeInSecondToLast === size) {
                return true;
            }
            // Fallback to last position for different formats
            const sizeInLast = parts[parts.length - 1];
            if (sizeInLast === size) {
                return true;
            }
        }
        return false;
    });
    
    return foundSKU;
};

const handleConfirmSelection = () => {
    const flattenedProducts = [];
    
    // Process multi-color selections
    Object.entries(selectedColorsAndSizes).forEach(([productId, colorSelections]) => {
        const product = products.find(p => p.id === productId);
        if (!product) return;
        
        Object.entries(colorSelections).forEach(([colorVariantIndex, colorData]) => {
            const variantIndex = parseInt(colorVariantIndex);
            const colorVariant = colorData.colorVariant || product.colorVariants?.[variantIndex];
            const sizes = colorData.sizes || [];
            
            if (!colorVariant || !sizes.length) return;
            
            sizes.forEach(size => {
                const selectedSKU = getSKUForSize(product, size, variantIndex);
                
                if (!selectedSKU) {
                    toast.error(`SKU not found for size ${size} in ${product.designName} - ${colorVariant.color}.`);
                    return;
                }
                
                const productEntry = {
                    ...product,
                    selectedSize: size,
                    selectedSKU,
                    selectedColorVariant: colorVariant,
                    quantity: 1 // Default quantity per product-size-color combo
                };
                
                flattenedProducts.push(productEntry);
            });
        });
    });
    
    // Process legacy single-color selections
    Object.entries(selectedProducts).forEach(([productId, productData]) => {
        const product = productData.product;
        const sizes = productData.sizes || [];
        
        if (!product || !sizes.length) return;
        
        sizes.forEach(size => {
            const selectedSKU = getSKUForSize(product, size);
            if (!selectedSKU) {
                toast.error(`SKU not found for size ${size} in ${product.designName}.`);
                return;
            }
            
            flattenedProducts.push({
                ...product,
                selectedSize: size,
                selectedSKU,
                quantity: 1 // Default quantity per product-size combo
            });
        });
    });

    // Check if we have any valid selections
    if (flattenedProducts.length === 0) {
        toast.warn("Please select at least one product with size(s)!");
        return;
    }

    // If any product is null (SKU missing), do not proceed
    if (flattenedProducts.some(p => p === null)) {
        return;
    }

    onSelectProduct(flattenedProducts);
    onClose();
};


    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={{
              overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 100 },
                content: {
                    top: '50%', left: '50%', right: 'auto', bottom: 'auto',
                    marginRight: '-50%', transform: 'translate(-50%, -50%)',
                    border: 'none', background: 'transparent', padding: '0',
                    width: '95%', maxWidth: '600px' // Can be narrower now
                }
            }}
            contentLabel="Select a Product"
        >
            <div className="bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-gray-200">
                {/* Modal Header */}
                 <div className="p-8 border-b-2 border-gray-200 flex justify-between items-center bg-white text-black">
                    <h2 className="text-2xl font-bold create-order-title">{title}</h2>
                     <button 
                         onClick={onClose} 
                           className="text-black bg-white hover:text-gray-600 hover:bg-gray-100 rounded-full p-3 transition-all duration-200 text-2xl font-bold leading-none group"
                     >
                         <span className="group-hover:rotate-90 transition-transform duration-200">×</span>
                     </button>
                </div>

                {/* Search Bar - Fixed Position */}
                 <div className="p-6 bg-gray-100 border-b-2 border-gray-300">
                    <input
                        type="text"
                        placeholder="Search by design name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                         className="w-full p-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all duration-200 bg-white shadow-sm text-lg font-medium"
                    />
                </div>

                {/* Scrollable Content Area */}
                <div className="flex-grow overflow-y-auto p-4 bg-gray-50">
                    <div className="space-y-4">
                        {filteredProducts.map((product) => {
                            const hasColorVariants = product.colorVariants && Array.isArray(product.colorVariants) && product.colorVariants.length > 0;
                            const isExpanded = expandedProducts[product.id];
                            const isLegacySelected = !!selectedProducts[product.id];
    const productImage = getProductImage(product);
                            const totalSelectedItems = getTotalSelectionsForProduct(product.id);

    return (
<div
  key={product.id}
  className={`p-4 border rounded-xl transition-all duration-300 ease-in-out transform hover:scale-[1.02] ${
                                        (isExpanded || isLegacySelected || totalSelectedItems > 0) 
                                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 shadow-lg' 
                                            : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
  }`}
>
  <div className="flex items-center gap-4">
    <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm">
      <img
        src={productImage}
        alt={product.designName}
        className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
        onError={(e) => {
          e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
        }}
      />
      <button
        onClick={e => {
                                                    e.stopPropagation();
          setViewImageUrl(productImage);
        }}
        title="View larger"
        className="absolute bottom-1 right-1 bg-black bg-opacity-60 text-white p-1.5 rounded-full hover:bg-opacity-80 transition-all duration-200 transform hover:scale-110"
        style={{ fontSize: '0.7rem', lineHeight: 1 }}
      >
        🔍
      </button>
    </div>

    <div className="cursor-pointer flex-1" onClick={() => toggleProduct(product)}>
                                            <p className="font-semibold text-gray-800">{product.designName}</p>
                                            <p className="text-sm text-gray-500">
                                                {hasColorVariants ? `${product.colorVariants.length} colors available` : 'Single color'}
                                            </p>
                                            {totalSelectedItems > 0 && (
                                                <p className="text-sm text-blue-600 font-medium">
                                                    {totalSelectedItems} item{totalSelectedItems > 1 ? 's' : ''} selected
                                                </p>
                                            )}
                                            {hasColorVariants && !isExpanded && totalSelectedItems === 0 && (
                                                <p className="text-xs text-gray-400 italic">Click to expand and select colors & sizes</p>
                                            )}
    </div>
  </div>

                                    {(isExpanded || isLegacySelected) && (
    <div className="mt-4 pt-4 border-t border-blue-200 animate-fadeIn">
                                            {hasColorVariants ? (
                                                // Multi-color selection interface
                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-between">
                                                        <p className="text-sm font-semibold text-gray-700">Select Colors & Sizes:</p>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setExpandedProducts(prev => ({ ...prev, [product.id]: false }));
                                                            }}
                                                            className="text-xs text-gray-500 hover:text-gray-700 underline"
                                                        >
                                                            Collapse
                                                        </button>
                                                    </div>
                                                    {product.colorVariants.map((variant, colorIndex) => {
                                                        const availableSizes = getAvailableSizesForColorVariant(product, colorIndex);
                                                        const selectedSizes = getSelectedSizesForColorVariant(product.id, colorIndex);
                                                        const isColorSelected = isColorVariantSelected(product.id, colorIndex);

                                                        return (
                                                            <div key={colorIndex} className="bg-gray-50 p-3 rounded-lg border">
                                                                <div className="flex items-center gap-3 mb-3">
                                                                    <div className="w-8 h-8 rounded-full border-2 border-gray-300 overflow-hidden">
                                                                        <img
                                                                            src={variant.images?.front || 'https://via.placeholder.com/32x32?text=No+Image'}
                                                                            alt={variant.color}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <p className="font-medium text-gray-800">{variant.color}</p>
                                                                        <p className="text-sm text-gray-500">₹{variant.totalPrice || variant.baseCost}</p>
                                                                    </div>
                                                                    {isColorSelected && (
                                                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                                            {selectedSizes.length} size{selectedSizes.length > 1 ? 's' : ''}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                
          <div className="flex flex-wrap gap-2">
                                                                    {availableSizes.map(size => {
                                                                        const isSizeSelected = selectedSizes.includes(size);
              return (
                <button
                                                                                key={size}
                  onClick={(e) => {
                    e.stopPropagation();
                                                                                    toggleSizeForColorVariant(product.id, colorIndex, size, variant);
                                                                                }}
                                                                                className={`px-3 py-1.5 text-xs border rounded-full font-medium transition-all duration-200 ease-in-out transform hover:scale-105 ${
                                                                                    isSizeSelected
                                                                                        ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                                                                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                                                                                }`}
                                                                            >
                                                                                {size}
                </button>
              );
            })}
          </div>
        </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                // Single color (legacy) interface
      <div>
                                                    <p className="text-sm font-semibold text-gray-700 mb-3">Select Size(s):</p>
        <div className="flex flex-wrap gap-2">
                                                        {getAvailableSizes(product).map(size => {
                                                            const isActive = selectedProducts[product.id]?.sizes?.includes(size) || false;
            return (
              <button
                key={size}
                onClick={(e) => {
                  e.stopPropagation();
                    toggleSize(product.id, size);
                }}
                className={`px-4 py-2 text-sm border rounded-full font-medium transition-all duration-200 ease-in-out transform hover:scale-105 ${
                  isActive
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                {size}
              </button>
            );
          })}
        </div>
      </div>
                                            )}
    </div>
  )}
</div>
    );
})}

                         {filteredProducts.length === 0 && (
                            <div className="text-center py-10 text-gray-500">
                                No products match your search.
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Footer */}
                 <div className="p-8 border-t-2 border-gray-200 text-right bg-white">
                    <button
                        onClick={handleConfirmSelection}
                        disabled={Object.keys(selectedColorsAndSizes).length === 0 && Object.keys(selectedProducts).length === 0}
                       className="bg-black text-white px-8 py-4 rounded-xl font-bold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl"
                    >
                       {buttonText}
                    </button>
                </div>
            </div>
{viewImageUrl && (
  <div
    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 pt-20" 
   style={{
    scrollbarWidth: 'none',        // Firefox
    msOverflowStyle: 'none',       // IE 10+
  }}>
    <button
      onClick={() => setViewImageUrl(null)}
      className="absolute top-5 right-5 text-white bg-black bg-opacity-60 hover:bg-opacity-90 rounded-full p-2 z-60"
      aria-label="Close image preview"
    >
      ✕
    </button>
    <img
      src={viewImageUrl}
      alt="Product Large View"
      className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-lg"
    />
  </div>
)}


        </Modal>

        


    );
};

export default ProductSelectionModal;